import React, { useState } from 'react';
import { Maximize2, X } from 'lucide-react';

interface SendConfig {
  prioritize: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom';
  ais: number;
  reasoning: 'default' | 'low' | 'medium' | 'high';
  webGrounding: boolean;
  selfeval: boolean;
  txt: boolean;
  enterSends: boolean;
  plusHumanEnabled?: boolean;
  humanLeaderboardEnabled?: boolean;
  selectedModels: Array<{
    openrouterId: string;
    name: string;
    creator: string;
  }>;
}

interface UserMessageDetailsProps {
  sendConfig: SendConfig;
  prodMode?: boolean;
}

/**
 * Display component for user message send configuration details
 * Shows all settings that were active when the user hit send
 */
export const UserMessageDetails: React.FC<UserMessageDetailsProps> = ({ sendConfig, prodMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formatBooleanValue = (value: boolean): string => {
    return value ? '✓ Enabled' : '✗ Disabled';
  };

  const formatPrioritize = (value: string): string => {
    const labels: Record<string, string> = {
      'free': 'Free',
      'speed': 'Speed',
      'balance': 'Balance',
      'intelligence': 'Intelligence',
      'cost': 'Cost'
    };
    return labels[value] || value;
  };

  const formatReasoning = (value: string): string => {
    const labels: Record<string, string> = {
      'default': 'Default',
      'low': 'Low',
      'medium': 'Medium',
      'high': 'High'
    };
    return labels[value] || value;
  };

  const renderDetailsContent = () => (
    <div className="space-y-4">
      {/* Priority Setting */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">Prioritize:</div>
        <div className="text-foreground/50">{formatPrioritize(sendConfig.prioritize)}</div>
      </div>

      {/* Number of AIs */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">AIs:</div>
        <div className="text-foreground/50">{sendConfig.ais}</div>
      </div>

      {/* Reasoning Effort */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">Reasoning:</div>
        <div className="text-foreground/50">{formatReasoning(sendConfig.reasoning)}</div>
      </div>

      {/* Web Grounding */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">Web Grounding:</div>
        <div className="text-foreground/50">{formatBooleanValue(sendConfig.webGrounding)}</div>
      </div>

      {/* Self-Eval */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">Self-Eval:</div>
        <div className="text-foreground/50">{formatBooleanValue(sendConfig.selfeval)}</div>
      </div>

      {/* TxT */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">TxT:</div>
        <div className="text-foreground/50">{formatBooleanValue(sendConfig.txt)}</div>
      </div>

      {/* +Human */}
      {sendConfig.plusHumanEnabled !== undefined && (
        <div className="flex items-start gap-3">
          <div className="font-semibold text-foreground/50 min-w-[120px]">+Human:</div>
          <div className="text-foreground/50">{formatBooleanValue(sendConfig.plusHumanEnabled)}</div>
        </div>
      )}

      {/* Leaderboard */}
      {sendConfig.plusHumanEnabled && sendConfig.humanLeaderboardEnabled !== undefined && (
        <div className="flex items-start gap-3">
          <div className="font-semibold text-foreground/50 min-w-[120px]">Leaderboard:</div>
          <div className="text-foreground/50">{formatBooleanValue(sendConfig.humanLeaderboardEnabled)}</div>
        </div>
      )}

      {/* Enter Sends */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">Send Option:</div>
        <div className="text-foreground/50">
          {sendConfig.enterSends ? 'Enter sends' : 'Shift+Enter sends'}
        </div>
      </div>

      {/* Selected Models */}
      <div className="flex items-start gap-3">
        <div className="font-semibold text-foreground/50 min-w-[120px]">Models:</div>
        <div className="flex-1">
          <div className="space-y-2">
            {sendConfig.selectedModels.map((model, index) => (
              <div key={model.openrouterId} className="flex items-start gap-2">
                <span className="text-foreground/50 text-xs">{index + 1}.</span>
                <div className="flex-1">
                  <div className="text-foreground/50 font-medium">{model.name}</div>
                  <div className="text-xs text-foreground/50">
                    {model.creator} • {model.openrouterId}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Hide View Details in production mode
  if (prodMode) {
    return null;
  }

  return (
    <>
      {/* Compact view */}
      <details className="mt-3 block" open={isOpen} onToggle={(e) => setIsOpen((e.target as HTMLDetailsElement).open)}>
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none text-right list-none block">
          {isOpen ? '▾' : '▸'} View Details
        </summary>
        <div className="relative w-full">
          <div className="mt-2 p-3 bg-background border border-border rounded text-xs leading-relaxed max-h-96 overflow-y-auto w-full">
            {renderDetailsContent()}
          </div>
          {isOpen && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="absolute top-4 right-4 p-1.5 bg-muted hover:bg-muted/80 rounded text-muted-foreground hover:text-foreground transition-colors"
              title="Expand to fullscreen"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </details>

      {/* Modal view */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-background border border-border rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">Message Send Configuration</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 hover:bg-muted rounded transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="text-sm leading-relaxed">
                {renderDetailsContent()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

